import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";

dayjs.extend(utc);

type ProductFeed = InterludeTypes["ProductFeed"];

const cssContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${theme.colors.white};
  border-radius: 8px;
  border: 1px solid ${theme.colors.staticFgLine};
`;

const cssTitle = css`
  font-weight: 600;
  font-size: 18px;
  color: ${theme.colors.staticFgPrimary};
  margin-bottom: 8px;
`;

const cssStatusRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const cssStatusText = css`
  color: ${theme.colors.staticFgPrimary};
`;

const cssNextSyncRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.colors.staticFgPrimary};
`;

const cssManualRefreshButton = css`
  width: fit-content;
`;

// Format date with time to YYYY-MM-DD HH:mm (in user's local timezone)
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "-";
  }
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Calculate next scheduled sync time (Base: +0800 01:30 daily = UTC 17:30 daily)
 *
 * Rules:
 * - If current time is after 17:30 UTC → next sync is next day's 17:30 UTC
 * - If current time is before 17:30 UTC → next sync is today's 17:30 UTC
 */

const SYNC_HOUR_UTC = 17;
const SYNC_MINUTE_UTC = 30;

const calculateNextScheduledSync = (
  lastSyncAt: string | null | undefined,
  currentTime: Date = new Date(),
): string | null => {
  if (!lastSyncAt) {
    return null;
  }

  const current = dayjs.utc(currentTime);
  const todayBaseSyncTime = current
    .startOf("day")
    .hour(SYNC_HOUR_UTC)
    .minute(SYNC_MINUTE_UTC);

  const nextSyncTime =
    current.isAfter(todayBaseSyncTime) || current.isSame(todayBaseSyncTime)
      ? todayBaseSyncTime.add(1, "day")
      : todayBaseSyncTime;

  return nextSyncTime.toISOString().replace(/\.\d{3}Z$/, "Z");
};

const StatusBadge: React.FC<{ status: ProductFeed["status"] }> = ({
  status,
}) => {
  const { t } = useTranslation();
  switch (status) {
    case "success":
      return (
        <Badge variant="positive">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="circle_check" />
            <span>{t("productFeed.status.success")}</span>
          </Stack>
        </Badge>
      );
    case "partial_success":
      return (
        <Badge variant="attentive">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="warning" />
            <span>{t("productFeed.status.partialSuccess")}</span>
          </Stack>
        </Badge>
      );
    case "error":
      return (
        <Badge variant="negative">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="circle_cross" />
            <span>{t("productFeed.status.error")}</span>
          </Stack>
        </Badge>
      );
    case "syncing":
      return (
        <Badge variant="superior">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="arrow_sync" />
            <span>{t("productFeed.status.syncing")}</span>
          </Stack>
        </Badge>
      );
    default:
      status satisfies never;
      throw new Error(inspectMessage`Invalid status ${status}`);
  }
};

interface SyncStatusProps {
  feed: ProductFeed;
  onManualRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

export const SyncStatus: React.FC<SyncStatusProps> = function SyncStatus({
  feed,
  onManualRefresh,
  isRefreshing = false,
}) {
  const { t } = useTranslation();
  const { status, lastSyncAt } = feed;

  const handleManualRefresh = useHandler(async () => {
    if (onManualRefresh) {
      await onManualRefresh();
    }
  });

  // Calculate next scheduled sync time
  const nextSyncTime = useMemo(
    () => calculateNextScheduledSync(lastSyncAt),
    [lastSyncAt],
  );

  // Don't show if feed hasn't been synced yet (no lastSyncAt)
  if (!lastSyncAt) {
    return null;
  }
  // Don't show next sync time when syncing
  const shouldShowNextSync = status !== "syncing" && nextSyncTime;

  return (
    <div css={cssContainer}>
      <Typography variant="h3" css={cssTitle}>
        {t("productFeed.syncStatus.title")}
      </Typography>

      <div css={cssStatusRow}>
        <StatusBadge status={status} />
        <Typography variant="body" css={cssStatusText}>
          {t("productFeed.table.column.lastSync")}: {formatDateTime(lastSyncAt)}
        </Typography>
      </div>

      {shouldShowNextSync && (
        <div css={cssNextSyncRow}>
          <MotifIcon un-i-motif="arrow_sync" />
          <Typography variant="body">
            {t("productFeed.syncStatus.nextScheduledSync")}:{" "}
            {formatDateTime(nextSyncTime)}
          </Typography>
        </div>
      )}

      <div css={cssManualRefreshButton}>
        <Button
          variant="plain"
          onClick={handleManualRefresh}
          loading={isRefreshing}
          disabled={isRefreshing || status === "syncing"}
        >
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="arrow_sync" />
            <span>{t("productFeed.syncStatus.manualRefresh")}</span>
          </Stack>
        </Button>
      </div>
    </div>
  );
};
